import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LicenseProvider } from "./contexts/LicenseContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { PermissionProvider } from "./contexts/PermissionContext";
import { Toaster } from "react-hot-toast";
import { Toaster as SonnerToaster } from "sonner";
import axios from "axios";
import { installOfflineInterceptors } from "./offline/interceptor";
import * as __offlineDB from "./offline/db";
import * as __offlineEntityMap from "./offline/entityMap";

// تفعيل نظام العمل Offline — يجب أن يكون قبل أي axios call
installOfflineInterceptors();

// Expose axios + offlineDB for E2E testing (non-production concern; these are read-only references)
if (typeof window !== 'undefined') {
  window.__axios = axios;
  window.__offlineDB = __offlineDB;
  window.__offlineEntityMap = __offlineEntityMap;
}

import { DashboardLayout } from "./components/layout/DashboardLayout";
import UpgradeModal from "./components/license/UpgradeModal";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PendingActivationPage from "./pages/PendingActivationPage";
import DashboardPage from "./pages/DashboardPage";
import InventoryPage from "./pages/InventoryPage";
import POSPage from "./pages/POSPage";
import SalesPage from "./pages/SalesPage";
import PurchasesPage from "./pages/PurchasesPage";
import CustomersPage from "./pages/CustomersPage";
import SuppliersPage from "./pages/SuppliersPage";
import EmployeesPage from "./pages/EmployeesPage";
import DamagesPage from "./pages/DamagesPage";
import ExpensesPage from "./pages/ExpensesPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import CustomerDebtsPage from "./pages/CustomerDebtsPage";
import SupplierDebtsPage from "./pages/SupplierDebtsPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import WhiteLabelSettingsPage from "./pages/WhiteLabelSettingsPage";
import AdsAdminPage from "./pages/AdsAdminPage";
import OnboardingPage from "./pages/OnboardingPage";
import AboutPage from "./pages/AboutPage";
import LegalAgreementPage from "./pages/LegalAgreementPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import SecurityPage from "./pages/SecurityPage";
import SecurityAdvancedPage from "./pages/SecurityAdvancedPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
// import LiveDashboard from "./pages/LiveDashboard"; // مدموج مع DashboardPage الآن (v5.10.3)
import CashRegisterPage from "./pages/CashRegisterPage";
import InstallmentSalesPage from "./pages/InstallmentSalesPage";
import ReturnsPage from "./pages/ReturnsPage";
import RolesPage from "./pages/RolesPage";
import EmployeesManagementPage from "./pages/EmployeesManagementPage";
import EmployeeRegisterPage from "./pages/EmployeeRegisterPage";
import LabelsSettingsPage from "./pages/LabelsSettingsPage";
import ThemeCustomizationPage from "./pages/ThemeCustomizationPage";
import InstallmentManagementPage from "./pages/InstallmentManagementPage";
import LicenseAdminPage from "./pages/LicenseAdminPage";
import ActivationHistoryPage from "./pages/ActivationHistoryPage";
import HeldInvoicesPage from "./pages/HeldInvoicesPage";
import PartnerLoginPage from "./pages/PartnerLoginPage";
import PartnerDashboardPage from "./pages/PartnerDashboardPage";
import PartnerManagementPage from "./pages/PartnerManagementPage";
import RemindersPage from "./pages/RemindersPage";
import MultiVouchersPage from "./pages/MultiVouchersPage";
import ReceiptPaymentVoucherPage from "./pages/ReceiptPaymentVoucherPage";
import DailyCashBoxPage from "./pages/DailyCashBoxPage";
import OverdueReportPage from "./pages/OverdueReportPage";
import InstallmentReportsPage from "./pages/InstallmentReportsPage";
import ExpiryAlertsPage from "./pages/ExpiryAlertsPage";
import BackupPage from "./pages/BackupPage";
import OwnerPanelPage from "./pages/OwnerPanelPage";
import SupportPage from "./pages/SupportPage";
import WelcomePage from "./pages/WelcomePage";
import InstallRequiredPage from "./pages/InstallRequiredPage";
import StoreSettingsPage from "./pages/StoreSettingsPage";
import AccountStatementPage from "./pages/AccountStatementPage";
import AuditLogPage from "./pages/AuditLogPage";
import DailyDateSelector, { isDailyDateSelected } from "./pages/DailyDateSelector";
import { PWAInstallPrompt } from "./components/pwa/PWAInstallPrompt";
import { UpdateNotification } from "./components/pwa/UpdateNotification";
import { OfflineNotice } from "./components/pwa/OfflineNotice";
import SessionWarning from "./components/security/SessionWarning";
import { ScheduledReportReminder } from "./components/pwa/ScheduledReportReminder";
import { InstallmentReminder } from "./components/pwa/InstallmentReminder";
import { WelcomeModal } from "./components/welcome/WelcomeModal";
import ErrorBoundary from "./components/ErrorBoundary";
import { useEffect, useState } from "react";

// تسجيل Service Worker لـ PWA
// نستخدم /sw.js (مسار جديد) لتجاوز Service Worker القديم المعطل عند العملاء القدامى
// الـ Killer SW على /service-worker.js يلغي نفسه ويمسح الكاش لمستخدمي النسخ القديمة
if ('serviceWorker' in navigator) {
  // 1. الاستماع لرسالة Killer SW (المستخدمون القدامى)
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SW_SELF_DESTRUCT') {
      console.log('[App] Killer SW finished cleanup. Reloading...');
      // مسح localStorage cache keys ذات الصلة بالنسخة (اختياري)
      try { localStorage.removeItem('app_version'); } catch (e) {}
      // إعادة تحميل صارمة بدون cache
      setTimeout(() => {
        window.location.replace(window.location.pathname + '?fresh=' + Date.now());
      }, 200);
    }
  });

  window.addEventListener('load', async () => {
    try {
      // 2. التحقق من SW القديم على /service-worker.js وإلغاء تسجيله إذا وُجد
      const oldRegs = await navigator.serviceWorker.getRegistrations();
      for (const reg of oldRegs) {
        const url = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
        if (url.endsWith('/service-worker.js')) {
          // اترك Killer SW يكمل عمله — سيلغي نفسه تلقائياً
          // لكن لو لم يحدث update لأي سبب، نُجبر التحديث
          try { await reg.update(); } catch (e) {}
        }
      }

      // 3. تسجيل SW الجديد على المسار الجديد /sw.js
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('SW registered (new path /sw.js): ', registration);

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New version available!');
            }
          });
        }
      });
    } catch (error) {
      console.log('SW registration failed: ', error);
    }
  });
}

// مكون لمنع الرجوع للخلف في المتصفح (لتجربة أفضل كتطبيق)
function usePreventBackNavigation() {
  useEffect(() => {
    const handlePopState = (e) => {
      // يمكن إضافة منطق هنا للتحكم في سلوك زر الرجوع
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
}

// مكون لإخفاء شريط العنوان عند التمرير (للأجهزة المحمولة)
function useHideAddressBar() {
  useEffect(() => {
    const scrollToHideAddressBar = () => {
      if (window.scrollY < 1) {
        window.scrollTo(0, 1);
      }
    };

    window.addEventListener('load', scrollToHideAddressBar);
    return () => window.removeEventListener('load', scrollToHideAddressBar);
  }, []);
}

// التحقق من تثبيت PWA
function isPWAInstalled() {
  // التحقق من وضع العرض standalone
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }
  // التحقق من iOS standalone
  if (window.navigator.standalone === true) {
    return true;
  }
  return false;
}

// مكون للتحقق من حالة المصادقة وتوجيه المستخدم
function AuthRedirect({ children }) {
  const { isAuthenticated, loading, isActive, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  // إذا كان المستخدم مسجلاً
  if (isAuthenticated) {
    // إذا غير مفعّل، وجهه لصفحة الانتظار
    if (!isActive) {
      return <Navigate to="/pending-activation" replace />;
    }
    // ✨ الكاشير يفتح مباشرة على POS (Kiosk Mode)
    if (user?.is_employee && user?.role === "cashier") {
      return <Navigate to="/pos" replace />;
    }
    // إذا مفعّل، وجهه للداشبورد
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// مكون للصفحات المحمية
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, isActive } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // إذا غير مفعّل، وجهه لصفحة الانتظار
  if (!isActive) {
    return <Navigate to="/pending-activation" replace />;
  }
  
  return children;
}

// قائمة البريد الإلكتروني للمالك - يتجاوز شاشة الشروط تلقائياً
const OWNER_EMAILS_FOR_BYPASS = ['aomerali42@gmail.com'];

// مكون للتحقق من قبول الاتفاقيات القانونية (أول شيء يظهر)
// ملاحظة: بعض الصفحات مثل forgot-password يجب أن تكون متاحة بدون الاتفاقية
function LegalAgreementGate({ children }) {
  const { user, isSuperAdmin, loading } = useAuth();
  const [legalAccepted, setLegalAccepted] = useState(() => {
    return localStorage.getItem('legal_agreement_accepted') === 'true';
  });

  // تجاوز للمالكين و Super Admins تلقائياً
  // نتحقق أيضاً من البريد المخزن في localStorage للحالة التي يكون فيها التحميل جارياً
  const storedUser = localStorage.getItem('user');
  let storedEmail = null;
  try {
    storedEmail = storedUser ? JSON.parse(storedUser)?.email?.toLowerCase() : null;
  } catch(e) {}
  const currentEmail = user?.email?.toLowerCase();
  const emailToCheck = currentEmail || storedEmail;
  
  const isOwner = emailToCheck && OWNER_EMAILS_FOR_BYPASS.includes(emailToCheck);
  const shouldBypass = isSuperAdmin || isOwner;
  
  // إعداد localStorage تلقائياً للمالكين
  useEffect(() => {
    if (shouldBypass && !legalAccepted) {
      localStorage.setItem('legal_agreement_accepted', 'true');
      localStorage.setItem('legal_agreement_date', new Date().toISOString());
      setLegalAccepted(true);
    }
  }, [shouldBypass, legalAccepted]);

  // التحقق من المسار الحالي - جميع المسارات ما عدا register و onboarding يمكنها تجاوز الشاشة
  // لأن ProtectedRoute سيتعامل مع المستخدمين غير المسجلين
  const currentPath = window.location.pathname;
  const requiresLegalPaths = ['/register', '/onboarding'];
  const requiresLegal = requiresLegalPaths.some(path => currentPath.startsWith(path));

  // إذا كان جاري التحميل، نعرض الأطفال مباشرة (لتجنب flash)
  // إذا كان المسار لا يتطلب شاشة الشروط أو المستخدم مالك/أدمين أو قبل الشروط مسبقاً
  if (loading || legalAccepted || !requiresLegal || shouldBypass) {
    return children;
  }

  return <LegalAgreementPage onAccept={() => {
    localStorage.setItem('legal_agreement_accepted', 'true');
    localStorage.setItem('legal_agreement_date', new Date().toISOString());
    setLegalAccepted(true);
  }} />;
}

// مكون للتحقق من اختيار تاريخ اليوم
// ملاحظة: تم تبسيط هذا الفلو — التاريخ يُحفظ تلقائياً عند الدخول (تاريخ ووقت اليوم الحالي)
// المستخدم يقدر يعدّله من إعدادات الجلسة لو محتاج بأثر رجعي.
function DailyDateGate({ children }) {
  const { loading } = useAuth();

  // حفظ التاريخ والوقت تلقائياً عند الدخول (مرة واحدة باليوم) — بدون أي شاشة منبثقة
  useEffect(() => {
    if (loading) return;
    const savedTimestamp = localStorage.getItem('daily_work_date_set');
    let alreadySetToday = false;
    if (savedTimestamp) {
      const savedDay = new Date(parseInt(savedTimestamp)).toDateString();
      const today = new Date().toDateString();
      alreadySetToday = savedDay === today;
    }
    if (!alreadySetToday) {
      const now = new Date();
      localStorage.setItem('daily_work_date', now.toISOString().split('T')[0]);
      localStorage.setItem('daily_work_time', `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`);
      localStorage.setItem('daily_work_date_set', Date.now().toString());
    }
  }, [loading]);

  return children;
}

// مكون لضمان قبول الاتفاقيات قبل صفحات معينة
function LegalRequiredRoute({ children }) {
  const [legalAccepted, setLegalAccepted] = useState(() => {
    return localStorage.getItem('legal_agreement_accepted') === 'true';
  });

  if (!legalAccepted) {
    return <LegalAgreementPage onAccept={() => setLegalAccepted(true)} />;
  }

  return children;
}

// مكون للتحقق من رؤية صفحة الترحيب أولاً
function WelcomeGate({ children }) {
  const welcomeSeen = sessionStorage.getItem('welcome_seen') === 'true';
  const hasToken = !!localStorage.getItem('token');
  const currentPath = window.location.pathname;

  // ✨ المسارات التي لا تحتاج عرض صفحة الترحيب (auth + register pages)
  const bypassPaths = [
    '/forgot-password',
    '/welcome',
    '/employee-register',
    '/login',           // ← دائماً اعرض صفحة تسجيل الدخول مباشرة
    '/register',        // ← دائماً اعرض صفحة التسجيل مباشرة
    '/partner-login',
    '/partner-dashboard',
  ];
  const shouldBypass = bypassPaths.some(path => currentPath.startsWith(path));

  if (shouldBypass || welcomeSeen || hasToken) {
    return children;
  }

  return <WelcomePage />;
}

// مكون لعرض صفحة الهبوط أو التوجيه للداشبورد إذا مسجل
function LandingPageWrapper() {
  const { isAuthenticated, loading, isActive } = useAuth();
  const navigate = (path) => window.location.href = path;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  // إذا كان المستخدم مسجلاً ومفعّلاً، وجهه للداشبورد
  if (isAuthenticated && isActive) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // إذا كان مسجلاً لكن غير مفعّل
  if (isAuthenticated && !isActive) {
    return <Navigate to="/pending-activation" replace />;
  }
  
  // عرض صفحة الهبوط للزوار
  return <LandingPage onGetStarted={() => window.location.href = '/login'} />;
}

function AppRoutes() {
  usePreventBackNavigation();
  useHideAddressBar();

  return (
    <>
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1e293b',
            color: '#fff',
            borderRadius: '8px',
            direction: 'rtl',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      {/* Sonner Toaster - بعض الصفحات تستخدم sonner بدل react-hot-toast */}
      <SonnerToaster position="top-center" theme="dark" richColors closeButton dir="rtl" />
      <PWAInstallPrompt />
      <Routes>
        {/* Welcome Page - First thing visitors see */}
        <Route path="/" element={<WelcomeGate><LandingPageWrapper /></WelcomeGate>} />
        
        {/* Home/Landing Page - After welcome */}
        <Route path="/home" element={<LandingPageWrapper />} />
        
        {/* Public Routes - redirect to dashboard if already logged in */}
        <Route path="/login" element={<WelcomeGate><AuthRedirect><LoginPage /></AuthRedirect></WelcomeGate>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/employee-register" element={<EmployeeRegisterPage />} />
        <Route path="/register" element={<WelcomeGate><LegalRequiredRoute><AuthRedirect><RegisterPage /></AuthRedirect></LegalRequiredRoute></WelcomeGate>} />
        <Route path="/pending-activation" element={<PendingActivationPage />} />
        <Route path="/onboarding" element={<LegalRequiredRoute><OnboardingPage /></LegalRequiredRoute>} />

        {/* Partner Routes (مستقلة عن باقي التطبيق) */}
        <Route path="/partner-login" element={<PartnerLoginPage />} />
        <Route path="/partner-dashboard" element={<PartnerDashboardPage />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<><WelcomeModal /><DashboardPage /></>} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/pos" element={<POSPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/purchases" element={<PurchasesPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/damages" element={<DamagesPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/returns" element={<ReturnsPage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/team" element={<EmployeesManagementPage />} />
          <Route path="/labels-settings" element={<LabelsSettingsPage />} />
          <Route path="/customer-debts" element={<CustomerDebtsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/supplier-debts" element={<SupplierDebtsPage />} />
          <Route path="/super-admin" element={<SuperAdminPage />} />
          <Route path="/ads-admin" element={<AdsAdminPage />} />
          <Route path="/whitelabel-settings" element={<WhiteLabelSettingsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/security-advanced" element={<SecurityAdvancedPage />} />
          <Route path="/live" element={<Navigate to="/dashboard" replace />} />
          <Route path="/cash-register" element={<CashRegisterPage />} />
          <Route path="/installments" element={<InstallmentSalesPage />} />
          <Route path="/installment-management" element={<InstallmentManagementPage />} />
          <Route path="/license-admin" element={<LicenseAdminPage />} />
          <Route path="/activation-history" element={<ActivationHistoryPage />} />
          <Route path="/held-invoices" element={<HeldInvoicesPage />} />
          <Route path="/reminders" element={<RemindersPage />} />
          <Route path="/multi-vouchers" element={<MultiVouchersPage />} />
          <Route path="/voucher" element={<ReceiptPaymentVoucherPage />} />
          <Route path="/cash-box" element={<DailyCashBoxPage />} />
          <Route path="/overdue-report" element={<OverdueReportPage />} />
          <Route path="/installment-reports" element={<InstallmentReportsPage />} />
          <Route path="/theme" element={<ThemeCustomizationPage />} />
          <Route path="/expiry-alerts" element={<ExpiryAlertsPage />} />
          <Route path="/backup" element={<BackupPage />} />
          <Route path="/owner-panel" element={<OwnerPanelPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/store-settings" element={<StoreSettingsPage />} />
          <Route path="/account-statement" element={<AccountStatementPage />} />
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/admin/partners" element={<PartnerManagementPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <LicenseProvider>
            <CurrencyProvider>
              <PermissionProvider>
                <BrowserRouter>
                  <LegalAgreementGate>
                    <DailyDateGate>
                      <AppRoutes />
                      <UpgradeModal />
                      <UpdateNotification />
                      <ScheduledReportReminder />
                      <InstallmentReminder />
                      <SessionWarning />
                      <PWAInstallPrompt />
                      <OfflineNotice />
                    </DailyDateGate>
                  </LegalAgreementGate>
                </BrowserRouter>
              </PermissionProvider>
            </CurrencyProvider>
          </LicenseProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
